import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FlaskConical, ShieldCheck, MessageSquare, Mail, CalendarDays } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAdminApi, btnPrimary, cardCls } from "./useAdminApi";
import { ReasonsPanel } from "./ReasonsPanel";
import { API, errMsg } from "@/lib/api";
import { useAdminApi as useApi } from "./useAdminApi";

const WEBHOOK_URL = `${API}/razorpay/webhook`;

const LastStatus = ({ s, testid, sentLabel = "delivered to Twilio" }) => s && (
  <p className={`text-xs mt-2 ${s.ok ? "text-emerald-700" : "text-red-600"}`} data-testid={testid}>
    Last attempt {new Date(s.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}: {s.ok ? sentLabel : `failed — ${s.error}`}
  </p>
);

const SmsCard = ({ s }) => {
  const call = useApi();
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(s.last_sms);
  const test = async () => {
    setBusy(true);
    try {
      const r = await call("post", "/admin/notify/test-sms");
      setLast({ ok: r.ok, error: r.error, at: new Date().toISOString() });
      if (r.ok) toast.success("Test alert sent to the clinic phone");
      else toast.error(`Twilio refused: ${r.error}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className={cardCls} data-testid="sms-card">
      <h3 className="text-xl font-semibold flex items-center gap-2"><MessageSquare className="h-5 w-5 text-teal" />SMS alerts</h3>
      <p className={`text-sm mt-2 ${s.sms_configured ? "text-ink" : "text-ink-muted"}`} data-testid="sms-status">
        {s.sms_configured ? "Keys added — the clinic (+91 6301262393) and the patient get an SMS for every confirmed, rescheduled or cancelled booking." : "Off — add Twilio keys to send booking SMS to patients and the clinic."}
      </p>
      {s.sms_configured && !s.sender_format_ok && (
        <p className="text-xs mt-2 text-amber-700" data-testid="sms-sender-warning">The sender number must be a number bought in Twilio, written with + and country code. The clinic's own number can't be the sender.</p>
      )}
      {s.sms_configured && last?.ok === false && <p className="text-xs mt-2 text-red-600 font-medium" data-testid="sms-needs-attention">Needs attention: messages are not reaching anyone yet.</p>}
      <LastStatus s={last} testid="sms-last-status" />
      {s.sms_configured && <button className={`${btnPrimary} mt-4`} onClick={test} disabled={busy} data-testid="sms-test-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send test alert to clinic</button>}
    </div>
  );
};

const WhatsAppCard = ({ configured, last, warn }) => {
  const call = useApi();
  const [busy, setBusy] = useState(false);
  const sendNow = async () => {
    setBusy(true);
    try { const r = await call("post", "/admin/reminders/send"); toast.success(`Sending reminders for ${r.target_date}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className={cardCls} data-testid="whatsapp-reminders-card">
      <h3 className="text-xl font-semibold">WhatsApp reminders</h3>
      <p className={`text-sm mt-2 ${configured ? "text-emerald-700" : "text-ink-muted"}`} data-testid="whatsapp-status">
        {configured ? "On — every evening at 7:00 PM, patients booked for the next day get a WhatsApp reminder." : "Off — add the Twilio WhatsApp sender number to turn on evening reminders."}
      </p>
      {configured && warn && <p className="text-xs mt-2 text-amber-700" data-testid="whatsapp-warning">Needs attention: Twilio can't send yet. Use a Twilio-approved WhatsApp sender (with + and country code) and finish Twilio's identity check.</p>}
      <LastStatus s={last} testid="whatsapp-last-status" />
      {configured && <button className={`${btnPrimary} mt-4`} onClick={sendNow} disabled={busy} data-testid="send-reminders-now">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send tomorrow's reminders now</button>}
    </div>
  );
};

const EmailCard = ({ s }) => {
  const call = useApi();
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(s.last_email);
  const test = async () => {
    setBusy(true);
    try {
      const r = await call("post", "/admin/notify/test-email");
      setLast({ ok: r.ok, error: r.error, at: new Date().toISOString() });
      if (r.ok) toast.success(`Test email sent to ${s.clinic_email}`);
      else toast.error(`Email failed: ${r.error}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(s.calendar_feed_url); toast.success("Calendar link copied"); };
  return (
    <div className={cardCls} data-testid="email-card">
      <h3 className="text-xl font-semibold flex items-center gap-2"><Mail className="h-5 w-5 text-teal" />Email alerts & calendar</h3>
      <p className={`text-sm mt-2 ${s.email_configured ? "text-ink" : "text-ink-muted"}`} data-testid="email-status">
        {s.email_configured
          ? `Every confirmed, rescheduled or cancelled booking is emailed to ${s.clinic_email} with the patient's name, phone, slot, reason and booking reference. Patients who shared an email get their own copy.`
          : "Off — the clinic email address is not set yet."}
      </p>
      {s.email_configured && last?.ok === false && <p className="text-xs mt-2 text-red-600 font-medium" data-testid="email-needs-attention">Needs attention: the last booking email did not go out.</p>}
      <LastStatus s={last} testid="email-last-status" sentLabel="email sent" />
      {s.email_configured && <button className={`${btnPrimary} mt-4`} onClick={test} disabled={busy} data-testid="email-test-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send test email to clinic</button>}
      {s.calendar_feed_url && (
        <div className="mt-4 rounded-2xl bg-alabaster border border-slate-200 p-3" data-testid="calendar-feed-info">
          <p className="text-xs font-semibold flex items-center gap-1"><CalendarDays className="h-4 w-4 text-teal" />Block your calendar automatically</p>
          <p className="text-[11px] text-ink-muted mt-1">Google Calendar → Other calendars → From URL → paste this private link. Every booking blocks the slot; rescheduled visits move and cancelled ones free up.</p>
          <code className="block mt-1 text-[11px] break-all bg-white border rounded-lg px-2 py-1" data-testid="calendar-feed-url">{s.calendar_feed_url}</code>
          <button type="button" onClick={copy} className="mt-2 text-xs font-semibold text-teal" data-testid="calendar-feed-copy">Copy link</button>
        </div>
      )}
    </div>
  );
};

const ModeOption = ({ active, onClick, icon: I, title, text, testid, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`text-left rounded-2xl border p-4 transition-colors disabled:opacity-50 ${active ? "border-teal bg-mint" : "border-slate-200 hover:bg-slate-50"}`}>
    <I className="h-5 w-5 text-teal" /><p className="font-semibold mt-2 text-sm">{title}</p><p className="text-xs text-ink-muted mt-1">{text}</p>
  </button>
);

export const SettingsPanel = () => {
  const call = useAdminApi();
  const [s, setS] = useState(null);
  const [fee, setFee] = useState("");
  const [busy, setBusy] = useState(false);
  const [reasonsKey, setReasonsKey] = useState(0);
  useEffect(() => { call("get", "/admin/settings").then((d) => { setS(d); setFee(String(d.consultation_fee)); }); }, [call]);

  const save = async (body, msg) => {
    setBusy(true);
    try { setS(await call("put", "/admin/settings", body)); toast.success(msg); setReasonsKey((k) => k + 1); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return <div className={`${cardCls} mt-4`}><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-4 mt-4" data-testid="settings-panel">
      <div className="space-y-4">
        <div className={cardCls}>
          <h3 className="text-xl font-semibold">Consultation fee</h3>
          <p className="text-sm text-ink-muted mt-1">Applies to every visit reason and the whole website.</p>
          <div className="flex gap-2 mt-4">
            <div className="flex h-10 rounded-xl border border-input overflow-hidden flex-1"><span className="px-3 grid place-items-center bg-slate-50 text-sm">₹</span>
              <input type="number" min="0" value={fee} onChange={(e) => setFee(e.target.value)} className="flex-1 px-3 text-sm outline-none" data-testid="fee-input" /></div>
            <button className={btnPrimary} disabled={busy || fee === ""} onClick={() => save({ consultation_fee: Number(fee) }, "Fee updated")} data-testid="fee-save-button">Save</button>
          </div>
        </div>
        <div className={cardCls}>
          <h3 className="text-xl font-semibold">Payment mode</h3>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <ModeOption active={s.razorpay_mode === "sandbox"} onClick={() => save({ razorpay_mode: "sandbox" }, "Demo payments on")} icon={FlaskConical} title="Sandbox / Demo" text="One-click test payments, no money moves" testid="mode-sandbox" />
            <ModeOption active={s.razorpay_mode === "live"} onClick={() => save({ razorpay_mode: "live" }, "Live Razorpay payments on")} icon={ShieldCheck} title="Live Razorpay" text="Real UPI, card & netbanking payments" testid="mode-live" />
          </div>
          <p className={`text-xs mt-3 ${s.live_keys_configured ? "text-emerald-700" : "text-amber-700"}`} data-testid="razorpay-keys-status">
            {s.live_keys_configured ? "Razorpay keys are connected." : "Razorpay keys not added yet — live mode stays off until they are."}
          </p>
          <p className="text-xs mt-1 text-ink-muted" data-testid="effective-mode">Patients currently see: {s.effective_payment_mode === "live" ? "Razorpay checkout" : "Demo checkout"}</p>
          <div className="mt-4 rounded-2xl bg-alabaster border border-slate-200 p-3" data-testid="webhook-info">
            <p className="text-xs font-semibold">Payment webhook {s.webhook_configured ? <span className="text-emerald-700">· connected</span> : <span className="text-amber-700">· secret not added</span>}</p>
            <p className="text-[11px] text-ink-muted mt-1">Razorpay Dashboard → Webhooks → add this URL with events payment.captured and order.paid:</p>
            <code className="block mt-1 text-[11px] break-all bg-white border rounded-lg px-2 py-1" data-testid="webhook-url">{WEBHOOK_URL}</code>
          </div>
        </div>
        <SmsCard s={s} />
        <EmailCard s={s} />
        <WhatsAppCard configured={s.whatsapp_configured} last={s.last_whatsapp} warn={!s.sender_format_ok || s.last_whatsapp?.ok === false} />
      </div>
      <ReasonsPanel key={reasonsKey} />
    </div>
  );
};
